import type { Badge } from "./badges";
import type { HubTab } from "./sheetGuard";
import type { MoreRowView } from "./moreSheet";
import { FeatureRegistry, HubApi, HubWidgetHandle, RowHome, ShopCurrencyPage, features, mergeBadge } from "../../features/registry";

/** What a currency pill is and where its tap goes, for its spoken name; an unknown pill reads its id. */
const PILL_NAMES: Readonly<Record<string, { readonly what: string; readonly opens: string }>> = {
  gems: { what: "Gems", opens: "open the Shop's Gems page" },
  marks: { what: "Marks", opens: "open the Shop's Marks page" },
};

/** "Gems: 1,940, open the Shop". */
export function pillName(id: string, value: string): string {
  const n = PILL_NAMES[id];
  return n ? `${n.what}: ${value}, ${n.opens}` : `${id}: ${value}`;
}

/** A row the hub itself adds to a list (Guide, Settings, Debug on the ⚙ menu; Iron on Events), placed by its MORE_ORDER slot. */
export interface CoreMoreRow extends MoreRowView { readonly order: number }

/**
 * The hub's side of the feature registry (features/registry.ts): the top-bar pills, the tab badges, a tab's page, the
 * listed rows (the ⚙ menu's and the Events tab's, by their `home`), the Shop and the widgets over the scene, drawn from
 * what the features registered. HubView holds one.
 */
export class HubFeatures {
  private handles: HubWidgetHandle[] = [];

  constructor(private readonly api: HubApi, private readonly registry: FeatureRegistry = features) {}

  /** The features' pills, after the resource pills: a real button when a tap opens something (VoiceOver can reach it). */
  pillsHtml(): string {
    return this.registry.topBarPills().map((p) => (p.onTap
      ? `<button type="button" class="fpill" data-pill="${p.id}">${p.html()}</button>`
      : `<span class="fpill" data-pill="${p.id}">${p.html()}</span>`)).join("");
  }

  bindPills(root: HTMLElement): void {
    for (const p of this.registry.topBarPills()) {
      const el = root.querySelector<HTMLElement>(`.fpill[data-pill="${p.id}"]`);
      if (!el || !p.onTap) continue;
      el.onclick = (e) => p.onTap!(this.api, el, e);
      el.setAttribute("aria-label", pillName(p.id, el.querySelector(".pill > span:not(.plus)")?.textContent?.trim() ?? ""));
    }
  }

  /** `core` with the features' red counts for `tab` added (registry.ts mergeBadge). */
  badge(tab: Exclude<HubTab, "battle">, core: Badge | null): Badge | null {
    return mergeBadge(core, this.registry.tabBadgeCount(tab));
  }

  /** Opens a feature's page for `tab`; false when no feature replaced it. */
  openPage(tab: HubTab): boolean {
    const page = this.registry.hubTabPageFor(tab);
    if (!page) return false;
    page.open(this.api);
    return true;
  }

  /** The visible feature rows listed at `home` (a row without one lives on the ⚙ menu) and the hub's own, by order. */
  moreRows(core: readonly CoreMoreRow[], home: RowHome = "menu"): MoreRowView[] {
    const fromFeatures = this.listed(home).map((r) => ({
      order: r.order, id: r.id, icon: r.icon, label: r.label(), note: r.note?.(), dot: r.dot?.() ?? 0, onClick: () => r.open(this.api),
    }));
    return [...fromFeatures, ...core].sort((a, b) => a.order - b.order);
  }

  /** The red dots of the visible feature rows at `home`: the Events tab's badge, the ⚙ button's dot. */
  moreDots(home: RowHome = "menu"): number {
    return this.listed(home).reduce((n, r) => n + Math.max(0, r.dot?.() ?? 0), 0);
  }

  private listed(home: RowHome) {
    return this.registry.moreRows().filter((r) => (r.home ?? "menu") === home && (r.visible?.() ?? true));
  }

  /** Opens the Shop tab on `page` (the economy feature's Shop; nothing without it). */
  openShop(page: ShopCurrencyPage): void { this.registry.openShop(this.api, page); }

  /** Whether an Iron challenge is open (the forge feature's free-route facts): the Events tab's Iron row says how. */
  ironOpen(): boolean { return this.registry.freeRewardsWaiting().ironOpen; }

  /** Mounts the widgets into the chip layer of a fresh render (the previous render's go first). */
  mountWidgets(layer: HTMLElement): void {
    this.disposeWidgets();
    // Re-entrancy (M30 #21): a widget's mount can re-render the hub (hub.changed), which mounts a full set of widgets
    // of its own; the outer pass then overwrote `handles` with its set and the inner set was never disposed — its
    // click listener on the UI root kept that whole HubView and HubScene (stand models, reduced copies) alive for the
    // session, ~20 MB of buffers a hub visit in the heap snapshots. An outer pass overtaken by a nested one now
    // drops what it made and leaves the nested set in place.
    const gen = ++this.gen;
    const made: HubWidgetHandle[] = [];
    for (const w of this.registry.hubWidgets()) {
      const h = w.mount(this.api, layer);
      if (gen !== this.gen) { h.dispose(); for (const x of made) x.dispose(); return; }
      made.push(h);
    }
    this.handles = made;
  }
  private gen = 0;
  /**
   * The hub's tick and layout, widget by widget. M44: a widget's update can re-render the hub (the achievements banner's
   * `hub.changed()` when one was just done), which disposes this set and mounts a new one; the loop then went on over the
   * old, disposed handles — the pets widget's `update` on its disposed tap set made fresh tap targets whose frame loops
   * nobody owned, one more per pet each time, running under every later battle and keeping that hub's scene alive. A
   * pass overtaken by a re-render now stops (the new set did its own first update as it mounted).
   */
  tickWidgets(): void { this.eachWidget((h) => h.update?.()); }
  layoutWidgets(): void { this.eachWidget((h) => h.layout?.()); }
  private eachWidget(f: (h: HubWidgetHandle) => void): void {
    const gen = this.gen;
    for (const h of this.handles) { if (gen !== this.gen) return; f(h); }
  }
  disposeWidgets(): void { this.gen++; for (const h of this.handles) h.dispose(); this.handles = []; }
}
